/**
 * Rights Transfer Component
 *
 * Custom left-right transfer UI for assigning rights to roles
 * Features: bulk selection, move all, search/filter, checkboxes
 */

import { useState, useMemo, useEffect } from 'react';
import { IconChevronRight, IconChevronLeft, IconChevronsRight, IconChevronsLeft } from '@tabler/icons-react';
import {
  Grid,
  Card,
  Stack,
  Group,
  Button,
  Text,
  Badge,
  ScrollArea,
  Checkbox,
  SearchInput,
} from '../../../../components/ui';
import type { RightsTransferProps, RightModelView } from '../types';

export function RightsTransfer({
  roleId,
  roleName,
  initialAvailable,
  initialAssigned,
  onChange,
}: RightsTransferProps) {
  // Local state for the transfer
  const [availableRights, setAvailableRights] = useState<RightModelView[]>(initialAvailable);
  const [assignedRights, setAssignedRights] = useState<RightModelView[]>(initialAssigned);
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedAssigned, setSelectedAssigned] = useState<number[]>([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  // Update when initial props change (only on mount or when role changes)
  useEffect(() => {
    setAvailableRights(initialAvailable);
    setAssignedRights(initialAssigned);
    // No onChange call here - just syncing with parent props, not a user action
  }, [initialAvailable, initialAssigned]);

  /**
   * Filter rights by search query
   */
  const filteredAvailable = useMemo(() => {
    if (!searchLeft || searchLeft.trim() === '') return availableRights;
    const query = searchLeft.toLowerCase().trim();
    return availableRights.filter(
      (r) =>
        r.name?.toLowerCase().includes(query) || r.description?.toLowerCase().includes(query)
    );
  }, [availableRights, searchLeft]);

  const filteredAssigned = useMemo(() => {
    if (!searchRight || searchRight.trim() === '') return assignedRights;
    const query = searchRight.toLowerCase().trim();
    return assignedRights.filter(
      (r) =>
        r.name?.toLowerCase().includes(query) || r.description?.toLowerCase().includes(query)
    );
  }, [assignedRights, searchRight]);

  /**
   * Move selected rights to assigned
   */
  const moveToAssigned = () => {
    const toMove = availableRights.filter((r) => selectedAvailable.includes(r.id));
    const newAssigned = [...assignedRights, ...toMove].sort((a, b) => a.name.localeCompare(b.name));
    setAssignedRights(newAssigned);
    setAvailableRights(availableRights.filter((r) => !selectedAvailable.includes(r.id)));
    const addedIds = toMove.map((r) => r.id);
    setSelectedAvailable([]);
    onChange?.(newAssigned, addedIds, []);
  };

  /**
   * Move all filtered rights to assigned
   */
  const moveAllToAssigned = () => {
    const toMove = searchLeft ? filteredAvailable : availableRights;
    const newAssigned = [...assignedRights, ...toMove].sort((a, b) => a.name.localeCompare(b.name));
    setAssignedRights(newAssigned);
    setAvailableRights(availableRights.filter((r) => !toMove.find((m) => m.id === r.id)));
    const addedIds = toMove.map((r) => r.id);
    setSelectedAvailable([]);
    onChange?.(newAssigned, addedIds, []);
  };

  /**
   * Move selected rights to available
   */
  const moveToAvailable = () => {
    const toMove = assignedRights.filter((r) => selectedAssigned.includes(r.id));
    const newAssigned = assignedRights.filter((r) => !selectedAssigned.includes(r.id));
    setAvailableRights([...availableRights, ...toMove].sort((a, b) => a.name.localeCompare(b.name)));
    setAssignedRights(newAssigned);
    const removedIds = toMove.map((r) => r.id);
    setSelectedAssigned([]);
    onChange?.(newAssigned, [], removedIds);
  };

  /**
   * Move all filtered rights to available
   */
  const moveAllToAvailable = () => {
    const toMove = searchRight ? filteredAssigned : assignedRights;
    const newAssigned = assignedRights.filter((r) => !toMove.find((m) => m.id === r.id));
    setAvailableRights([...availableRights, ...toMove].sort((a, b) => a.name.localeCompare(b.name)));
    setAssignedRights(newAssigned);
    const removedIds = toMove.map((r) => r.id);
    setSelectedAssigned([]);
    onChange?.(newAssigned, [], removedIds);
  };

  /**
   * Handle checkbox change for available rights
   */
  const handleAvailableCheckboxChange = (rightId: number, checked: boolean) => {
    if (checked) {
      setSelectedAvailable([...selectedAvailable, rightId]);
    } else {
      setSelectedAvailable(selectedAvailable.filter((id) => id !== rightId));
    }
  };

  /**
   * Handle checkbox change for assigned rights
   */
  const handleAssignedCheckboxChange = (rightId: number, checked: boolean) => {
    if (checked) {
      setSelectedAssigned([...selectedAssigned, rightId]);
    } else {
      setSelectedAssigned(selectedAssigned.filter((id) => id !== rightId));
    }
  };

  return (
    <Grid gutter="md">
      {/* Available Rights (Left) */}
      <Grid.Col span={5}>
        <Card withBorder padding="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Available Rights
              </Text>
              <Badge size="sm" variant="light">
                {filteredAvailable.length}
              </Badge>
            </Group>

            <SearchInput
              placeholder="Search rights..."
              value={searchLeft}
              onChange={setSearchLeft}
            />

            <ScrollArea h={400}>
              <Stack gap="xs">
                {filteredAvailable.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="xl">
                    {searchLeft ? 'No rights found' : 'All rights assigned'}
                  </Text>
                ) : (
                  filteredAvailable.map((right) => (
                    <Checkbox
                      key={right.id}
                      checked={selectedAvailable.includes(right.id)}
                      onChange={(e) => handleAvailableCheckboxChange(right.id, e.target.checked)}
                      label={
                        <Stack gap={2}>
                          <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                            {right.name}
                          </Text>
                          {right.description && (
                            <Text size="xs" c="dimmed">
                              {right.description}
                            </Text>
                          )}
                        </Stack>
                      }
                    />
                  ))
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        </Card>
      </Grid.Col>

      {/* Transfer Buttons (Middle) */}
      <Grid.Col span={2}>
        <Stack gap="xs" justify="center" style={{ height: '100%', paddingTop: '80px' }}>
          <Button
            variant="light"
            onClick={moveAllToAssigned}
            disabled={filteredAvailable.length === 0}
            fullWidth
            title="Move all to assigned"
          >
            <IconChevronsRight size={18} />
          </Button>
          <Button
            variant="light"
            onClick={moveToAssigned}
            disabled={selectedAvailable.length === 0}
            fullWidth
            title="Move selected to assigned"
          >
            <IconChevronRight size={18} />
          </Button>
          <Button
            variant="light"
            onClick={moveToAvailable}
            disabled={selectedAssigned.length === 0}
            fullWidth
            title="Move selected to available"
          >
            <IconChevronLeft size={18} />
          </Button>
          <Button
            variant="light"
            onClick={moveAllToAvailable}
            disabled={filteredAssigned.length === 0}
            fullWidth
            title="Move all to available"
          >
            <IconChevronsLeft size={18} />
          </Button>
        </Stack>
      </Grid.Col>

      {/* Assigned Rights (Right) */}
      <Grid.Col span={5}>
        <Card withBorder padding="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Assigned Rights
              </Text>
              <Badge size="sm" variant="light" color="blue">
                {filteredAssigned.length}
              </Badge>
            </Group>

            <SearchInput
              placeholder="Search rights..."
              value={searchRight}
              onChange={setSearchRight}
            />

            <ScrollArea h={400}>
              <Stack gap="xs">
                {filteredAssigned.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="xl">
                    {searchRight ? 'No rights found' : 'No rights assigned'}
                  </Text>
                ) : (
                  filteredAssigned.map((right) => (
                    <Checkbox
                      key={right.id}
                      checked={selectedAssigned.includes(right.id)}
                      onChange={(e) => handleAssignedCheckboxChange(right.id, e.target.checked)}
                      label={
                        <Stack gap={2}>
                          <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                            {right.name}
                          </Text>
                          {right.description && (
                            <Text size="xs" c="dimmed">
                              {right.description}
                            </Text>
                          )}
                        </Stack>
                      }
                    />
                  ))
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
